import React, { useState } from "react";
import { ANYskinResult } from "../types";
import AdviceBlock from "./AdviceBlock";
import { CheckCircle, Info, ShieldAlert, XCircle } from "lucide-react";
import { formatRubles, roundTo } from "../utils/math_helpers";

interface Props {
  data: ANYskinResult;
  // «Подробнее у ассистента» в рекомендациях — без колбэка кнопка не показывается.
  onAskAssistant?: (question: string) => void;
}

export default function ANYskinTab({ data, onAskAssistant }: Props) {
  const [showStopDetails, setShowStopDetails] = useState(true);

  // Helper for color matching
  const getRatingStyle = (rating: string) => {
    switch (rating) {
      case "AAA":
      case "AA":
        return { bg: "bg-emerald-50 text-emerald-800 border-emerald-200", badge: "bg-emerald-500", label: "Превосходная кредитоспособность", text: "text-emerald-600" };
      case "A":
      case "BBB":
        return { bg: "bg-teal-50 text-teal-800 border-teal-200", badge: "bg-teal-500", label: "Хорошая кредитоспособность", text: "text-teal-600" };
      case "BB":
      case "B":
        return { bg: "bg-orange-50 text-orange-800 border-orange-200", badge: "bg-orange-500", label: "Приемлемая финансовая стабильность", text: "text-orange-500" };
      case "C":
        return { bg: "bg-amber-50 text-amber-900 border-amber-200", badge: "bg-amber-600", label: "Высокая долговая уязвимость", text: "text-amber-600" };
      default:
        return { bg: "bg-rose-50 text-rose-950 border-rose-200", badge: "bg-rose-600", label: "Критический финансовый риск / Банкротство", text: "text-rose-600" };
    }
  };

  const style = getRatingStyle(data.rating);

  return (
    <div className="space-y-6">
      {/* Prime Header Medal Card */}
      <div className={`p-6 rounded-2xl border ${style.bg} flex flex-col md:flex-row items-center justify-between gap-6 shadow-md shadow-slate-100/40 transition-all duration-300 hover:shadow-lg`}>
        <div className="space-y-2.5 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start">
            <span className={`w-2.5 h-2.5 rounded-full ${style.badge} animate-pulse`}></span>
            <span className="text-[10px] font-mono tracking-widest uppercase font-bold opacity-80">Методика ANYskin</span>
          </div>
          <h2 className="text-xl md:text-2xl font-extrabold font-display tracking-tight text-slate-900 leading-snug">
            Рейтинг защищенности: {style.label}
          </h2>
          <p className="text-xs leading-relaxed opacity-95 max-w-2xl text-slate-600 font-medium">
            Комплексная оценка включает финансовые пропорции баланса по РСБУ (G1), параметры контрактного портфеля по 44/223-ФЗ (G2) и стоп-факторы судебной или оборотной уязвимости (USL).
          </p>
        </div>
        <div className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl border border-slate-100 min-w-[160px] shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
          <span className="text-5xl font-black font-display tracking-tighter text-slate-950">{data.rating}</span>
          <span className="text-[10px] font-mono font-bold text-slate-450 uppercase tracking-wider mt-2 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-md">
            {data.totalScore} / 20.00 <span className="text-[9px] font-sans font-semibold text-slate-400">баллов</span>
          </span>
        </div>
      </div>

      {/* G1 & G2 Breakdown Scorecard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Component G1: Financial Ratios */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4 transition-all duration-300 hover:shadow-md">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h3 className="font-extrabold font-display text-slate-900 flex items-center gap-2 text-base">
              <span className="text-indigo-600 font-mono text-xs font-bold bg-indigo-50 px-2 py-1 rounded-md">G1</span>
              Финансовый блок (Баланс по РСБУ)
            </h3>
            <span className="text-xs font-mono font-extrabold text-slate-500">
              Ср. балл: <span className="text-indigo-600 font-bold">{data.g1Score}</span> / 10
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Q1 */}
            <div className="p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-indigo-150 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex justify-between items-center">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider">Q1: ДОЛГ / КАПИТАЛ</span>
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-200/50 px-1.5 py-0.2 rounded uppercase">{data.details.Q1.unit}</span>
              </div>
              <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                <span className="text-base font-black text-slate-900">{data.details.Q1.value}</span>
                <span className="text-[11px] text-indigo-700 font-extrabold bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                  +{data.details.Q1.score} <span className="text-[9px] font-sans font-medium text-indigo-400">{data.details.Q1.scoreUnit}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed pt-0.5">{data.details.Q1.label}</p>
            </div>

            {/* Q2 */}
            <div className="p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-indigo-150 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex justify-between items-center">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider">Q2: ДОЛГ / EBITDA</span>
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-200/50 px-1.5 py-0.2 rounded uppercase">{data.details.Q2.unit}</span>
              </div>
              <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                <span className="text-base font-black text-slate-900">{data.details.Q2.value}</span>
                <span className="text-[11px] text-indigo-700 font-extrabold bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                  +{data.details.Q2.score} <span className="text-[9px] font-sans font-medium text-indigo-400">{data.details.Q2.scoreUnit}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed pt-0.5">{data.details.Q2.label}</p>
            </div>

            {/* Q3 */}
            <div className="p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-indigo-150 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex justify-between items-center">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider">Q3: ТЕКУЩАЯ ЛИКВИДНОСТЬ</span>
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-200/50 px-1.5 py-0.2 rounded uppercase">{data.details.Q3.unit}</span>
              </div>
              <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                <span className="text-base font-black text-slate-900">{data.details.Q3.value}</span>
                <span className="text-[11px] text-indigo-700 font-extrabold bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                  +{data.details.Q3.score} <span className="text-[9px] font-sans font-medium text-indigo-400">{data.details.Q3.scoreUnit}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed pt-0.5">{data.details.Q3.label}</p>
            </div>

            {/* Q4 */}
            <div className="p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-indigo-150 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex justify-between items-center">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider">Q4: ОБЕСПЕЧЕННОСТЬ СОС</span>
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-200/50 px-1.5 py-0.2 rounded uppercase">{data.details.Q4.unit}</span>
              </div>
              <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                <span className="text-base font-black text-slate-900">{data.details.Q4.value}</span>
                <span className="text-[11px] text-indigo-700 font-extrabold bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                  +{data.details.Q4.score} <span className="text-[9px] font-sans font-medium text-indigo-400">{data.details.Q4.scoreUnit}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed pt-0.5">{data.details.Q4.label}</p>
            </div>

            {/* Q5 */}
            <div className="p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-indigo-150 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex justify-between items-center">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider">Q5: МАРЖИНАЛЬНОСТЬ EBITDA</span>
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-200/50 px-1.5 py-0.2 rounded uppercase">{data.details.Q5.unit === "коэф" ? "%" : "%"}</span>
              </div>
              <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                <span className="text-base font-black text-slate-900">{(data.details.Q5.value * 100).toFixed(1)}%</span>
                <span className="text-[11px] text-indigo-700 font-extrabold bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                  +{data.details.Q5.score} <span className="text-[9px] font-sans font-medium text-indigo-400">{data.details.Q5.scoreUnit}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed pt-0.5">{data.details.Q5.label}</p>
            </div>

            {/* Q6 */}
            <div className="p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-indigo-150 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex justify-between items-center">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider">Q6: ВЫРУЧКА / ЛИМИТ</span>
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-200/50 px-1.5 py-0.2 rounded uppercase">{data.details.Q6.unit}</span>
              </div>
              <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                <span className="text-base font-black text-slate-900">
                  {data.details.Q6.value === 99 ? "N/A" : `${data.details.Q6.value}x`}
                </span>
                <span className="text-[11px] text-indigo-700 font-extrabold bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                  +{data.details.Q6.score} <span className="text-[9px] font-sans font-medium text-indigo-400">{data.details.Q6.scoreUnit}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed pt-0.5">{data.details.Q6.label}</p>
            </div>
          </div>
        </div>

        {/* Component G2: Contract Portfolio & Profile */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4 transition-all duration-300 hover:shadow-md">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h3 className="font-extrabold font-display text-slate-900 flex items-center gap-2 text-base">
              <span className="text-emerald-600 font-mono text-xs font-bold bg-emerald-50 px-2 py-1 rounded-md">G2</span>
              Контрактный блок (44/223-ФЗ, Опыт)
            </h3>
            <span className="text-xs font-mono font-extrabold text-slate-500">
              Ср. балл: <span className="text-emerald-600 font-bold">{data.g2Score}</span> / 10
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Q2_1 */}
            <div className="h-full p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 flex flex-col justify-between group">
              <div className="space-y-1">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider block">Q2_1: СРОК НА РЫНКЕ</span>
                <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                  <span className="text-base font-extrabold text-slate-900 leading-none">{data.details.Q2_1.value} <span className="text-xs font-sans font-medium text-slate-450">л.</span></span>
                  <span className="text-[10.5px] font-mono font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded">+{data.details.Q2_1.score}б</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-snug pt-1">{data.details.Q2_1.label}</p>
            </div>

            {/* Q2_2 */}
            <div className="h-full p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 flex flex-col justify-between group">
              <div className="space-y-1">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider block">Q2_2: КРУПНЫЙ КОНТРАКТ</span>
                <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                  <span className="text-base font-extrabold text-slate-900 leading-none">{data.details.Q2_2.value ? "Да" : "Нет"}</span>
                  <span className="text-[10.5px] font-mono font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded">+{data.details.Q2_2.score}б</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-snug pt-1">{data.details.Q2_2.label}</p>
            </div>

            {/* Q2_3 */}
            <div className="h-full p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 flex flex-col justify-between group">
              <div className="space-y-1">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider block">Q2_3: КОЛ-ВО ГОСЗАКАЗОВ</span>
                <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.5">
                  <span className="text-base font-extrabold text-slate-900 leading-none">{data.details.Q2_3.value} <span className="text-xs font-sans font-medium text-slate-450">шт.</span></span>
                  <span className="text-[10.5px] font-mono font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded">+{data.details.Q2_3.score}б</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-snug pt-1">{data.details.Q2_3.label}</p>
            </div>

            {/* Q2_4 */}
            <div className="h-full p-3.5 bg-slate-50 border border-slate-100/70 rounded-xl space-y-2 hover:bg-white hover:border-emerald-200 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 flex flex-col justify-between group">
              <div className="space-y-1">
                <span className="t-meta font-mono font-bold text-slate-400 uppercase tracking-wider block">Q2_4: СРЕДНИЙ ЧЕК ЗАКАЗА</span>
                <div className="flex justify-between items-baseline font-mono border-b border-slate-100 pb-1.55">
                  <span className="text-xs font-black text-slate-950 font-mono tracking-tight">{formatRubles(data.details.Q2_4.value)}</span>
                  <span className="text-[10.5px] font-mono font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded">+{data.details.Q2_4.score}б</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 font-medium leading-snug pt-1">{data.details.Q2_4.label}</p>
            </div>
          </div>

          <div className="pt-2">
            <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/80 flex items-start gap-3">
              <Info className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
              <div className="text-xs text-indigo-850 leading-relaxed space-y-1 font-medium">
                <p className="font-extrabold text-slate-900">Замещающий оборотный поток:</p>
                <p>
                  Средний исполненный заказ составляет <strong className="font-mono text-indigo-900 bg-white px-1.5 py-0.5 rounded border border-indigo-100/30">{formatRubles(data.avgContractRub)}</strong> при общей выручке заемщика <strong className="font-mono text-indigo-900 bg-white px-1.5 py-0.5 rounded border border-indigo-100/30">{formatRubles(data.revenueCurrRub)}</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stop Factors (USL Block) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <button 
          onClick={() => setShowStopDetails(!showStopDetails)}
          className="w-full flex justify-between items-center p-5 font-bold font-display text-slate-900 border-b border-slate-50 bg-slate-50/40 hover:bg-slate-50/80 transition-all text-left"
        >
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-500 shrink-0" />
            <span className="font-extrabold text-sm md:text-base">Угрозы и Стоп-факторы риска (Блок USL)</span>
            {data.uslScore < 0 && (
              <span className="bg-rose-150 text-rose-900 text-[10px] font-mono font-extrabold px-2.5 py-0.5 rounded-lg border border-rose-250">
                вычет: {data.uslScore}б
              </span>
            )}
          </div>
          <span className="text-xs text-indigo-600 font-bold hover:text-indigo-800 transition-colors uppercase tracking-wider font-mono">
            {showStopDetails ? "Свернуть" : "Подробнее"}
          </span>
        </button>

        {showStopDetails && (
          <div className="p-6 divide-y divide-slate-100/70">
            {data.details.stopFactors.map((factor) => (
              <div 
                key={factor.code}
                className={`py-3.5 flex items-start justify-between gap-4 transition-all duration-200 hover:bg-slate-50/50 -mx-6 px-6 ${factor.active ? "bg-rose-50/20 -mx-6 px-6" : ""}`}
              >
                <div className="flex items-start gap-3">
                  {factor.active ? (
                    <XCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  )}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200/50 text-slate-600">
                        {factor.code}
                      </span>
                      <p className={`text-sm tracking-tight ${factor.active ? "font-bold text-rose-950" : "text-slate-600 font-medium"}`}>
                        {factor.label}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {factor.active ? (
                    <span className="font-mono text-xs text-rose-600 font-extrabold bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100">-{factor.deduction} баллов</span>
                  ) : (
                    <span className="text-[10px] font-mono font-bold bg-slate-50 border border-slate-100 px-2 py-0.5 rounded text-slate-400">чисто</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <AdviceBlock conclusion={data.conclusion} recommendations={data.recommendations} onAskAssistant={onAskAssistant} />
    </div>
  );
}
